import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import store from "src/page/store";
import { immediateToast } from "izitoast-react";
import { refreshSession } from "src/utils/utils";
import { getConfig, AppConfig } from "src/config/Config";
import { logout } from "src/page/auth/loginSlice";

let internalApiInstance: AxiosInstance | null = null;

export function getInternalClient(): AxiosInstance {
  if (internalApiInstance) {
    return internalApiInstance;
  }

  const config: AppConfig = getConfig();

  const api = axios.create({
    baseURL: config.INTERNAL_API_KEY,
    headers: {
      "Content-Type": "application/json",
      partner_id: config.PARTNER_ID
    }
  });

  api.interceptors.request.use(async (configuration: InternalAxiosRequestConfig) => {
    try {
      const {
        auth: { cognitoIdToken }
      } = store.getState();

      if (cognitoIdToken) {
        configuration.headers = configuration.headers || {};
        configuration.headers["Authorization"] = `Bearer ${cognitoIdToken}`;
      }
    } catch (error) {
      console.error(error);
    }
    return configuration;
  });

  api.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response.data.errors) {
        const errorMessage = response.data.errors[0].message;
        let messageObj: { message?: string } | undefined;

        try {
          messageObj = JSON.parse(errorMessage);
        } catch (error) {
          console.log(error);
          messageObj = undefined;
        }

        immediateToast("error", {
          message: messageObj?.message ?? errorMessage,
          timeout: 3000,
          position: "topCenter"
        });
        throw new Error(response.data.errors[0]);
      }
      return response.data;
    },
    async function (error: any) {
      const originalRequest: InternalAxiosRequestConfig = error.config;
      const {
        auth: { cognitoRefreshToken }
      } = store.getState();
      const dispatch = store.dispatch;

      if (error.response?.status === 401) {
        const originalRequestWithRetry = originalRequest as InternalAxiosRequestConfig & { _retry?: number };

        if (originalRequestWithRetry && (!originalRequestWithRetry._retry || originalRequestWithRetry._retry < 4)) {
          originalRequestWithRetry._retry = (originalRequestWithRetry._retry || 0) + 1;

          if (cognitoRefreshToken != null) {
            try {
              const session = await refreshSession(cognitoRefreshToken);

              if (originalRequestWithRetry.headers) {
                originalRequestWithRetry.headers["Authorization"] =
                  `Bearer ${(session as unknown as { idToken: string }).idToken}`;
              }

              return axios(originalRequestWithRetry);
            } catch (err: any) {
              immediateToast("error", {
                message: err.message,
                timeout: 3000,
                position: "topCenter"
              });
              dispatch(logout());
              return Promise.reject(err);
            }
          } else {
            immediateToast("error", {
              message: "No refresh token available, please login again",
              timeout: 3000,
              position: "topCenter"
            });
            dispatch(logout());
            return Promise.reject(error);
          }
        } else {
          immediateToast("error", {
            message: "your session expired, please login again",
            timeout: 3000,
            position: "topCenter"
          });
          dispatch(logout());
          return Promise.reject(error);
        }
      } else {
        immediateToast("error", {
          message: error.message || "An unexpected error occurred",
          timeout: 3000,
          position: "topCenter"
        });
        return Promise.reject(error);
      }
    }
  );

  internalApiInstance = api;
  return internalApiInstance;
}

const internalClient = (data: { query: string; variables?: any }) => {
  return getInternalClient().post("/graphql", data);
};

export default internalClient;
